/**
 * Utilitaires de validation SIRET
 * Implémente l'algorithme de Luhn pour valider les SIRET
 */

/**
 * Normalise un SIRET en supprimant espaces et tirets
 * @param siret SIRET brut
 * @returns SIRET normalisé (chiffres uniquement)
 */
export const normalizeSiret = (siret: string): string => {
  return siret.replace(/[\s-]/g, '');
};

/**
 * Valide un SIRET avec l'algorithme de Luhn
 * @param siret SIRET à valider (14 chiffres)
 * @returns true si le SIRET est valide, false sinon
 */
export const validateSiretLuhn = (siret: string): boolean => {
  // Normaliser le SIRET
  const normalized = normalizeSiret(siret);

  // Vérifier que c'est bien 14 chiffres
  if (normalized.length !== 14) {
    return false;
  }

  // Vérifier que ce sont bien des chiffres
  if (!/^\d+$/.test(normalized)) {
    return false;
  }

  // Algorithme de Luhn
  let sum = 0;
  let shouldDouble = false;

  // Parcourir de droite à gauche
  for (let i = normalized.length - 1; i >= 0; i--) {
    let digit = parseInt(normalized.charAt(i), 10);

    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    shouldDouble = !shouldDouble;
  }

  // Le SIRET est valide si la somme est divisible par 10
  return sum % 10 === 0;
};

/**
 * Formatte un SIRET pour l'affichage (avec espaces)
 * @param siret SIRET à formater
 * @returns SIRET formaté (ex: "732 829 320 00074")
 */
export const formatSiret = (siret: string): string => {
  const normalized = normalizeSiret(siret);
  if (normalized.length !== 14) {
    return siret;
  }
  return `${normalized.slice(0, 3)} ${normalized.slice(3, 6)} ${normalized.slice(6, 9)} ${normalized.slice(9, 14)}`;
};

/**
 * Valide un SIRET complet (format et Luhn)
 * @param siret SIRET à valider
 * @returns Objet avec isValid et message d'erreur si applicable
 */
export const validateSiret = (siret: string): { isValid: boolean; error?: string } => {
  if (!siret || siret.trim() === '') {
    return { isValid: false, error: 'Le SIRET est requis' };
  }

  const normalized = normalizeSiret(siret);

  if (normalized.length !== 14) {
    return { isValid: false, error: 'Le SIRET doit contenir exactement 14 chiffres' };
  }

  if (!/^\d+$/.test(normalized)) {
    return { isValid: false, error: 'Le SIRET ne doit contenir que des chiffres' };
  }

  if (!validateSiretLuhn(siret)) {
    return { isValid: false, error: 'Le SIRET fourni n\'est pas valide (échec de validation Luhn)' };
  }

  return { isValid: true };
};
